import { useState } from 'react';
import { Plus, Pencil, Trash2, Target, Calendar, DollarSign } from 'lucide-react';
import Table from '../components/Table.tsx';
import Modal from '../components/Modal.tsx';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { 
  useContributionDrives, 
  useCreateContributionDrive, 
  useUpdateContributionDrive, 
  useDeleteContributionDrive 
} from '../hooks/useContributionDrives.ts';
import { useSubgroups } from '../hooks/useSubgroups.ts';
import { useGetProfile } from '../hooks/useProfile.ts';
import { PermissionPage, PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

const driveTypeOptions = [
  { value: 'regular', label: 'Regular Contribution' },
  { value: 'emergency', label: 'Emergency Fund' },
  { value: 'project', label: 'Project Fund' },
  { value: 'celebration', label: 'Celebration Fund' },
  { value: 'one_off', label: 'One Off Payments' },
  { value: 'custom', label: 'Custom Drive' },
];

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'completed', label: 'Completed' },
];

export default function ContributionDrivesPage() {
  const { can } = usePermissions();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDrive, setSelectedDrive] = useState(null);
  const isAdmin = useIsAdmin();

  // API hooks
  const { data: drivesData, isLoading } = useContributionDrives();
  const createDrive = useCreateContributionDrive();
  const updateDrive = useUpdateContributionDrive();
  const deleteDrive = useDeleteContributionDrive();

  const drives = drivesData?.data || [];

  const columns = [
    { 
      label: 'Category Name', 
      field: 'title', 
      sortable: true,
      render: row => (
        <div>
          <div className="font-medium text-gray-900">{row.title}</div>
          <div className="text-sm text-gray-500">{row.type || 'No type specified'}</div>
        </div>
      )
    },
    { 
      label: 'Type', 
      field: 'type', 
      sortable: true,
      render: row => {
        const type = driveTypeOptions.find(opt => opt.value === row.type);
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            row.type === 'regular' ? 'bg-blue-100 text-blue-800' :
            row.type === 'emergency' ? 'bg-red-100 text-red-800' :
            row.type === 'project' ? 'bg-green-100 text-green-800' :
            row.type === 'celebration' ? 'bg-purple-100 text-purple-800' :
            row.type === 'one_off' ? 'bg-orange-100 text-orange-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {type?.label || row.type || 'Custom'}
          </span>
        );
      }
    },
    { 
      label: 'Amount per Member', 
      field: 'amount_per_member', 
      sortable: true,
      render: row => row.amount_per_member ? `Ksh ${row.amount_per_member.toLocaleString()}` : 'Not set'
    },
    { 
      label: 'Target Amount', 
      field: 'target_amount', 
      sortable: true,
      render: row => row.target_amount ? `Ksh ${row.target_amount.toLocaleString()}` : 'Not set'
    },
    { 
      label: 'Status', 
      field: 'status', 
      sortable: true,
      render: row => {
        const status = statusOptions.find(opt => opt.value === row.status);
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            row.status === 'active' ? 'bg-green-100 text-green-800' :
            row.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
            'bg-blue-100 text-blue-800'
          }`}>
            {status?.label || row.status}
          </span>
        );
      }
    },
    {
      label: 'Payment Account',
      field: 'mpesa_credential_id',
      sortable: true,
      render: row => {
        const cred = row.mpesa_credential;
        if (!cred && !row.mpesa_credential_id) {
          return <span className="text-xs text-gray-400 italic">Default Paybill</span>;
        }
        const label = cred?.account_name || (cred?.paybill_number ? `Paybill ${cred.paybill_number}` : `Account #${row.mpesa_credential_id}`);
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
            {label}
          </span>
        );
      }
    },
    { 
      label: 'Created', 
      field: 'created_at', 
      sortable: true,
      render: row => (
        <div className="text-sm">
          {row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}
        </div>
      )
    },
    {
      label: 'Actions',
      field: 'actions',
      render: (row) => (
        <div className="flex space-x-2">
          {can(PERMISSIONS.EDIT_CONTRIBUTION_DRIVE) && (
          <button 
            className="text-blue-600 hover:text-blue-800" 
            onClick={() => { setSelectedDrive(row); setIsModalOpen(true); }}
            title="Edit Category"
          >
            <Pencil size={18} />
          </button>
          )}
          {can(PERMISSIONS.DELETE_CONTRIBUTION_DRIVE) && (
          <button 
            className="text-red-600 hover:text-red-800" 
            onClick={() => handleDeleteDrive(row.id)}
            title="Delete Category"
          >
            <Trash2 size={18} />
          </button>
          )}
        </div>
      )
    },
  ];

  const handleCreateOrUpdateDrive = (drive) => {
    if (selectedDrive) {
      // Update existing drive
      updateDrive.mutate({ id: selectedDrive.id, ...drive }, {
        onSuccess: () => {
          setIsModalOpen(false);
          setSelectedDrive(null);
        }
      });
    } else {
      // Create new drive
      createDrive.mutate(drive, {
        onSuccess: () => {
          setIsModalOpen(false);
        }
      });
    }
  };

  const handleDeleteDrive = (id) => {
    if (window.confirm('Are you sure you want to delete this contribution category?')) {
      deleteDrive.mutate(id);
    }
  };

  return (
    <PermissionPage permission={PERMISSIONS.VIEW_CONTRIBUTIONS}>
    <div className="p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Contribution Categories</h2>
          <p className="text-gray-600 mt-1">Manage contribution categories and fundraising types</p>
        </div>
        <PermissionGate permission={PERMISSIONS.CREATE_CONTRIBUTION_DRIVE}>
          <button 
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto transition-colors" 
            onClick={() => { setSelectedDrive(null); setIsModalOpen(true); }}
          >
            <Plus size={16} className="mr-2" /> Create Category
          </button>
        </PermissionGate>
      </div>
      
              <Table columns={columns} data={drives} loading={isLoading} />
      
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); setSelectedDrive(null); }}
        title={selectedDrive ? 'Edit Contribution Category' : 'Create Contribution Category'}
      >
        <DriveForm
          initialData={selectedDrive}
          onSubmit={handleCreateOrUpdateDrive}
          onCancel={() => { setIsModalOpen(false); setSelectedDrive(null); }}
          isLoading={createDrive.isPending || updateDrive.isPending}
        />
      </Modal>
    </div>
    </PermissionPage>
  );
}

function DriveForm({ initialData, onSubmit, onCancel, isLoading }) {
  const { data: profile } = useGetProfile();
  const { data: subgroupsData } = useSubgroups();
  
  const currentGroup = profile?.data?.current_group;
  const isHouseManagement = Number(currentGroup?.is_house_management) === 1;
  const houseLabel = isHouseManagement ? (currentGroup?.house_label || 'House') : 'Subgroup';
  const rawSubgroups = subgroupsData?.data || [];
  const subgroups = rawSubgroups.filter(s => s.type !== 'village');
  
  const [name, setName] = useState(initialData?.title || '');
  const [type, setType] = useState(initialData?.type || 'regular');
  const [amountPerMember, setAmountPerMember] = useState(initialData?.amount_per_member || '');
  const [targetAmount, setTargetAmount] = useState(initialData?.target_amount || '');
  const [status, setStatus] = useState(initialData?.status || 'active');
  const [advancedAssignment, setAdvancedAssignment] = useState(!!initialData?.subgroup_targets);
  const [subgroupTargets, setSubgroupTargets] = useState(initialData?.subgroup_targets || {});

  const mpesaCredentialsList = profile?.data?.all_mpesa_credentials || [];
  const [mpesaCredentialId, setMpesaCredentialId] = useState(initialData?.mpesa_credential_id || '');

  const handleSubgroupTargetChange = (subgroupId, value) => {
    setSubgroupTargets(prev => ({
      ...prev,
      [subgroupId]: value ? parseFloat(value) : null
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ 
      title: name, 
      type, 
      amount_per_member: amountPerMember ? parseFloat(amountPerMember) : null, 
      target_amount: targetAmount ? parseFloat(targetAmount) : null,
      status,
      subgroup_targets: advancedAssignment ? subgroupTargets : null,
      mpesa_credential_id: mpesaCredentialId ? parseInt(mpesaCredentialId) : null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Category Name <span className="text-red-500">*</span></label>
        <input 
          type="text" 
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
          value={name} 
          onChange={e => setName(e.target.value)} 
          required 
          placeholder="Enter category name"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Category Type <span className="text-red-500">*</span></label>
        <select 
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
          value={type} 
          onChange={e => setType(e.target.value)} 
          required
        >
          {driveTypeOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {mpesaCredentialsList.length > 1 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Collect Payments Into (M-Pesa Account)
          </label>
          <select 
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={mpesaCredentialId || ''}
            onChange={e => setMpesaCredentialId(e.target.value)}
          >
            <option value="">Default (Group Primary Account)</option>
            {mpesaCredentialsList.map(cred => (
              <option key={cred.id} value={cred.id}>
                {cred.account_name || (cred.paybill_number ? `Paybill ${cred.paybill_number}` : `Till ${cred.shortcode}`)}
                {cred.is_default ? ' (Default)' : ''} - {cred.paybill_number || cred.shortcode}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Choose which Paybill or Till will collect funds for this specific category.
          </p>
        </div>
      )}
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Amount per Member (Ksh)</label>
        <input 
          type="number" 
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
          value={amountPerMember} 
          onChange={e => setAmountPerMember(e.target.value)} 
          min={0}
          step={0.01}
          disabled={advancedAssignment}
          placeholder="Enter amount per member (optional)"
        />
        <p className="text-xs text-gray-500 mt-1">Leave empty if no fixed amount per member</p>
      </div>

      {subgroups.length > 0 && (
        <div className="pt-2 border-t border-gray-100">
          <div className="flex items-center mb-3">
            <input 
              type="checkbox" 
              id="advancedAssignment"
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" 
              checked={advancedAssignment}
              onChange={(e) => setAdvancedAssignment(e.target.checked)}
            />
            <label htmlFor="advancedAssignment" className="ml-2 text-sm font-medium text-gray-700">
              Advanced Assignment (Set different targets per {houseLabel.toLowerCase()})
            </label>
          </div>

          {advancedAssignment && (
            <div className="bg-gray-50 p-4 rounded-md space-y-3 mt-2 border border-gray-200">
              <p className="text-xs text-gray-600 mb-2">
                Specify exactly how much each member in the following {houseLabel.toLowerCase()}s should contribute for this drive. 
                Members in {houseLabel.toLowerCase()}s without a specific target will fall back to the global "Amount per Member" above.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-2">
                {subgroups.map(subgroup => (
                  <div key={subgroup.id} className="flex flex-col">
                    <label className="text-xs font-medium text-gray-700 mb-1 truncate" title={subgroup.name}>
                      {subgroup.name}
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">Ksh</span>
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="pl-10 w-full border border-gray-300 rounded-md py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Target amount"
                        value={subgroupTargets[subgroup.id] || ''}
                        onChange={(e) => handleSubgroupTargetChange(subgroup.id, e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Target Amount (Ksh)</label>
        <input 
          type="number" 
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
          value={targetAmount} 
          onChange={e => setTargetAmount(e.target.value)} 
          min={0}
          step={0.01}
          placeholder="Enter target amount (optional)"
        />
        <p className="text-xs text-gray-500 mt-1">Leave empty if no target amount is needed</p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Status <span className="text-red-500">*</span></label>
        <select 
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
          value={status} 
          onChange={e => setStatus(e.target.value)} 
          required
        >
          {statusOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button 
          type="button" 
          className="px-4 py-2 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors" 
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : (initialData ? 'Update' : 'Create')} Category
        </button>
      </div>
    </form>
  );
} 